import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusIcon, MinusIcon } from '@heroicons/react/24/outline';
import SpeakIt from './CompAISpeakIt';

// Animation variants for smooth transitions
const variants = {
    hidden: { opacity: 0, height: 0, overflow: 'hidden', marginTop: 0, marginBottom: 0, paddingTop: 0, paddingBottom: 0 },
    visible: { opacity: 1, height: 'auto', overflow: 'visible', marginTop: '1rem', marginBottom: '1rem', paddingTop: '1rem', paddingBottom: '1rem' },
    exit: { opacity: 0, height: 0, overflow: 'hidden', marginTop: 0, marginBottom: 0, paddingTop: 0, paddingBottom: 0 }
};

// Helper function to render the believability gradient scale
const renderBelievabilityScale = (rating) => {
    const percentage = Math.max(0, Math.min(100, (rating / 5) * 100));
    const gradientStyle = {
        background: `linear-gradient(to right, hsl(197.7, 86.7%, 46.9%) ${percentage}%, rgb(44, 44, 44) ${percentage}%)`
    };

    return (
        <div className="flex items-center">
            <div className="w-24 h-2 rounded-full overflow-hidden bg-zinc-600 mr-2" style={gradientStyle}>
            </div>
            <span className="text-zinc-300 text-sm">({rating}/5)</span>
        </div>
    );
};

// Helper function to get descriptive text for believability score
const getBelievabilityText = (rating) => {
    if (rating === undefined || rating === null) return '';
    if (rating <= 1.5) return 'Very Low Fit';
    if (rating <= 2.5) return 'Low Fit';
    if (rating <= 3.5) return 'Moderate Fit';
    if (rating <= 4.5) return 'High Fit';
    return 'Very High Fit';
};

const ValueItemCard = ({ item }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showBelievabilityReasoning, setShowBelievabilityReasoning] = useState(false);

    if (!item) return null;

    const {
        categoryOfValue,
        businessBenefit,
        why,
        roi,
        roiReasoning = [],
        objections = [],
        believability = {}
    } = item;
    const { believability: believabilityScore, believabilityReasoning = [] } = believability;

    const hasExpandableContent = why || roiReasoning.length > 0 || believabilityReasoning.length > 0 || objections.length > 0;

    return (
        <div className="rounded-lg overflow-hidden bg-black/20 border border-zinc-700/20 flex flex-col h-fit duration-300">
            <div className="p-6 md:p-8 flex-grow">


                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                    <div className="flex-1">
                        <h3 className="text-zinc-50 font-normal text-xl md:text-2xl mb-4">{categoryOfValue}</h3>
                        {roi && (
                            <div className="flex">
                                <p className="text-sky-300 text-lg md:text-lg font-normal">{roi}</p>
                            </div>
                        )}
                    </div>

                    {believabilityScore !== undefined && (
                        <div className="flex-shrink-0 flex flex-col items-center w-56 bg-zinc-800/25 rounded-lg p-4 gap-y-2 border border-zinc-700/10">
                            <h4 className="text-zinc-100 mb-1 text-base text-center tracking-wider uppercase">{getBelievabilityText(believabilityScore)}</h4>
                            <div className="flex justify-center">
                                {renderBelievabilityScale(believabilityScore)}
                            </div>
                            <div className="mt-2 w-full flex justify-center ">
                                <button
                                    onClick={() => setShowBelievabilityReasoning(!showBelievabilityReasoning)}
                                    className="flex items-center w-full justify-center rounded-md mt-2 bg-sky-600/30 hover:bg-sky-500/40 py-1.5 text-sky-300 text-xs font-medium transition-colors duration-200 px-3"
                                    aria-label="Show analysis detail breakdown"
                                >
                                    <span>Why this score?</span>
                                    {showBelievabilityReasoning ? (
                                        <MinusIcon className="w-3 h-3 ml-1.5" />
                                    ) : (
                                        <PlusIcon className="w-3 h-3 ml-1.5" />
                                    )}
                                </button>


                            </div>
                        </div>
                    )}
                </div>


                <AnimatePresence>
                    {showBelievabilityReasoning && (
                        <motion.div
                            initial={{ opacity: 0, height: 0, y: -10 }}
                            animate={{ opacity: 1, height: 'auto', y: 0 }}
                            exit={{ opacity: 0, height: 0, y: -10 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="mb-6 bg-zinc-800/40 rounded-lg shadow-md p-4 text-left"
                        >
                            <h4 className="text-zinc-200 font-medium mb-2 text-sm">Believability Reasoning:</h4>
                            <ul className="list-disc list-outside pl-4 space-y-1.5">
                                {believabilityReasoning.map((reason, idx) => (
                                    <li key={`bel-${idx}`} className="text-zinc-400 text-xs md:text-sm">
                                        {reason}
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    )}
                </AnimatePresence>

                {businessBenefit && (
                    <div className="mb-6">
                        <p className="text-zinc-300 text-base md:text-base leading-relaxed">{businessBenefit}</p>
                    </div>
                )}

                <div className="flex justify-center pt-4 mt-6">
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="flex items-center justify-center rounded-md bg-sky-500/5 hover:bg-sky-400/25 py-2.5 text-zinc-50 text-sm font-medium transition-colors duration-200 px-6 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-zinc-700 disabled:text-zinc-400 border border-sky-500/10"
                        aria-label={isExpanded ? "Hide Details" : "Show Details"}
                        disabled={!hasExpandableContent}
                    >
                        {isExpanded ? 'Hide Details' : 'Show More Details'}
                        {isExpanded ? (
                            <MinusIcon className="w-4 h-4 ml-2" />
                        ) : (
                            <PlusIcon className="w-4 h-4 ml-2" />
                        )}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {isExpanded && hasExpandableContent && (
                    <motion.div
                        key="content"
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        variants={variants}
                        transition={{ duration: 0.35, ease: "easeInOut" }}
                        className="overflow-hidden px-6 md:px-8 pb-6 md:pb-8 bg-zinc-800/30 border-t border-zinc-700"
                    >
                        <>
                            {why && (
                                <div className="mb-6 mt-4">
                                    <h4 className="text-zinc-100 font-semibold text-base md:text-lg mb-2">Why This Matters</h4>
                                    <p className="text-zinc-300 text-sm md:text-base leading-relaxed">{why}</p>
                                </div>
                            )}

                            <div className="grid grid-cols-1 lg:grid-cols-1 gap-x-8 gap-y-8 pt-6">
                                <div className="space-y-8">
                                    {roiReasoning.length > 0 && (
                                        <div>
                                            <h4 className="text-zinc-100 font-semibold text-base md:text-lg mb-3">ROI Reasoning</h4>
                                            <div className="overflow-x-auto rounded-md border border-zinc-700">
                                                <table className="w-full ">
                                                    <thead className="bg-zinc-800/50">
                                                        <tr>
                                                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider w-1/2">Reasoning</th>
                                                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider w-1/2">Est. ROI Impact</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-zinc-700/70">
                                                        {roiReasoning.map(({ roiReasoning: reason, roi: roiImpact }, idx) => (
                                                            <tr key={`roi-${idx}`} className="hover:bg-black/10 transition-colors">
                                                                <td className="px-4 py-3 text-zinc-300 text-sm md:text-base align-top w-1/2">{reason}</td>
                                                                <td className="px-4 py-3 text-zinc-300 text-sm md:text-base align-top w-1/2">{roiImpact}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-8">
                                    {objections.length > 0 && (
                                        <div>
                                            <h4 className="text-zinc-100 font-semibold text-base md:text-lg mb-3">Potential Objections & Handling</h4>
                                            <div className="overflow-x-auto rounded-md border border-zinc-700">
                                                <table className="w-full">
                                                    <thead className="bg-zinc-800/50">
                                                        <tr>
                                                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Objection</th>
                                                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Handling</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-zinc-700/70">
                                                        {objections.map(({ objection, objectionHandling }, idx) => (
                                                            <tr key={`obj-${idx}`} className="hover:bg-black/10 transition-colors">
                                                                <td className="px-4 py-3 text-zinc-300 text-sm md:text-base font-medium align-top">{objection}</td>
                                                                <td className="px-4 py-3 text-zinc-300 text-sm md:text-base align-top">{objectionHandling}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const PathValueProposition = ({ valuePropositionData, eli5BusinessValue }) => {
    const valueItems = valuePropositionData?.valueItems;
    //console.log('eli5BusinessValue', eli5BusinessValue);

    if (!Array.isArray(valueItems) || valueItems.length === 0) {
        return  <div className="flex flex-col items-center justify-center h-full text-zinc-400 p-8 text-center min-h-[calc(100vh-10rem)] bg-zinc-950">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-16 h-16 mb-4 text-zinc-500">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
        </svg>
        <h3 className="text-xl font-semibold text-zinc-200 mb-2">No Value Proposition Available</h3>
        <p className="text-zinc-400">There are currently no value proposition details to display.</p>
    </div>
    }

    const maxScrollHeight = 'calc(100vh - 10rem)';

    return (
        <div
            className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 px-4 pb-8 overflow-y-auto"
            style={{ maxHeight: maxScrollHeight }}
        >


            {/* Narrative Story Section */}
            {eli5BusinessValue && (
                <div className="mb-6 col-span-1 lg:col-span-2 pl-8 pt-4 overflow-x-hidden">
                    <div className="flex justify-between items-start">
                        <div className="w-9/12 pr-12">
                            {/* <h3 className="text-4xl text-zinc-50 mb-4 sp-big-heading">Value Summary</h3> */}
                            <h4 className="text-3xl font-base leading-10 text-zinc-50 mb-6">
                                {eli5BusinessValue?.eli5Data?.headline}
                            </h4>
                            <p className="text-zinc-300 text-base md:text-lg leading-relaxed">
                                {eli5BusinessValue?.eli5Data?.simpleExplanation}
                            </p>
                            
                        </div>

                        <div className="w-3/12 flex justify-end pl-4 pt-2 mr-8">
                            <div className="pt-6 w-full">
                                <SpeakIt textToSpeak={eli5BusinessValue?.eli5Data?.simpleExplanation}
                                    title="Value Summary" />
                            </div>
                        </div>
                        
                    </div>
                    {/* <div className="flex justify-between items-start">
                         
                        <div className="w-full flex justify-end   pt-2">
                            <p className="text-zinc-300 text-base md:text-lg leading-relaxed">
                                {eli5BusinessValue?.eli5Data?.simpleExplanation}
                            </p>
                        </div>
                    </div> */}
                </div>
            )}


            {valueItems.map((item, index) => (
                <ValueItemCard key={item.id || `vp-${index}`} item={item} />
            ))}
        </div>
    );
};

export default PathValueProposition;